// src/components/Sidebar.js
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import useUserRole from "../hooks/useUserRole";
import useRolePermissions from "../hooks/useRolePermissions";
import "./Sidebar.css";

export default function Sidebar() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  const { role } = useUserRole();
  const { can } = useRolePermissions(role);

  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);

      if (!u) {
        setProfile(null);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        setProfile(snap.exists() ? snap.data() : null);
      } catch (err) {
        console.error("Failed to load user profile:", err);
        setProfile(null);
      }
    });

    return () => unsub();
  }, []);

  const isSignedIn = !!user;

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Sign out failed:", err);
    }
  };

  const cx = ({ isActive }) => (isActive ? "active" : "");

  const displayName =
    profile?.displayName ||
    user?.displayName ||
    (user?.email ? user.email.split("@")[0] : "User");

  const email = profile?.email || user?.email || "";
  const photoURL = profile?.photoURL || user?.photoURL || "";
  const position = profile?.position || "—";

  const payRate =
    profile?.payRate !== undefined &&
    profile?.payRate !== null &&
    profile?.payRate !== ""
      ? `$${Number(profile.payRate).toFixed(2)}/hr`
      : "—";

  const hasPhoto = !!photoURL;

  const canSeeAdminPortal =
    can("FullScheduleRead") ||
    can("DailyOps") ||
    can("ApplicationsRead") ||
    can("EmployeesRead") ||
    can("EditPunches") ||
    can("TimeCardsRead") ||
    can("PayRates") ||
    can("TournamentDates") ||
    can("ScheduleControl") ||
    can("PermissionsEdit") ||
    can("DispatchConsole");

  if (!isSignedIn) return null;

  return (
    <aside className="sidebar">
      <div className="sidebar-header sidebar-header--blank" />

      <div className="sidebar-usercard">
        {hasPhoto ? (
          <img className="sidebar-avatar" src={photoURL} alt="User" />
        ) : (
          <div
            className="sidebar-avatar sidebar-avatar--placeholder"
            aria-label="No photo"
          >
            <span className="sidebar-avatarIcon">👤</span>
          </div>
        )}

        <div className="sidebar-name">{displayName}</div>

        <div className="sidebar-email" title={email}>
          {email}
        </div>

        <div className="sidebar-meta">
          <div className="sidebar-meta-row">
            <span>Position</span>
            <strong>{position}</strong>
          </div>
          <div className="sidebar-meta-row">
            <span>Pay Rate</span>
            <strong>{payRate}</strong>
          </div>
        </div>

        <button
          type="button"
          className="sidebar-profile-btn"
          onClick={() => navigate("/profile")}
        >
          Update Profile
        </button>
      </div>

      <nav className="nav">
        <NavLink to="/dashboard" end className={cx}>
          Dashboard
        </NavLink>

        <NavLink to="/punch" className={cx}>
          Punch Clock
        </NavLink>

        <NavLink to="/timecard" className={cx}>
          Time Card
        </NavLink>

        {can("StatusBoard") && (
          <NavLink to="/dispatch" className={cx}>
            Status Board
          </NavLink>
        )}

        {canSeeAdminPortal && (
          <>
            <div
              className="nav-section-label"
              style={{ marginTop: 14, opacity: 0.8 }}
            >
              Admin Portal
            </div>

            <NavLink to="/admin" end className={cx}>
              Admin Home
            </NavLink>

            {can("FullScheduleRead") && (
              <NavLink to="/admin/schedule" className={cx}>
                Schedule
              </NavLink>
            )}

            <NavLink to="/admin/hq" className={cx}>
            HQ Tracking
            </NavLink>

            {can("DailyOps") && (
              <NavLink to="/admin/daily-schedule" className={cx}>
                Daily Ops
              </NavLink>
            )}

            {can("ApplicationsRead") && (
              <NavLink to="/admin/applications" className={cx}>
                Applications
              </NavLink>
            )}

            {can("EmployeesRead") && (
              <NavLink to="/admin/employees" className={cx}>
                Employees
              </NavLink>
            )}

            {can("EditPunches") && (
              <NavLink to="/admin/edit-punches" className={cx}>
                Edit Punches
              </NavLink>
            )}

            {can("TimeCardsRead") && (
              <NavLink to="/admin/timecards" className={cx}>
                Timecards
              </NavLink>
            )}

            {can("PayRates") && (
              <NavLink to="/admin/system/pay-rates" className={cx}>
                Pay Rates
              </NavLink>
            )}

            {can("TournamentDates") && (
              <NavLink to="/admin/tournament-dates" className={cx}>
                Tournament Dates
              </NavLink>
            )}

            {can("ScheduleControl") && (
              <NavLink to="/admin/system-controls" className={cx}>
                System Controls
              </NavLink>
            )}

            {can("PermissionsEdit") && (
              <NavLink to="/admin/permissions" className={cx}>
                Permissions
              </NavLink>
            )}

            {can("DispatchConsole") && (
              <NavLink to="/admin/dispatch" className={cx}>
                Dispatch Console
              </NavLink>
            )}
          </>
        )}

        <button
          type="button"
          className="sidebar-profile-btn sidebar-signout-btn"
          onClick={handleSignOut}
        >
          Sign out
        </button>
      </nav>
    </aside>
  );
}